// Based on https://stackoverflow.com/a/29304414
// Browser support notes are for desktop browsers only.
// All mobile browsers use the Safari 9 fallback.
export const downloadCsvFile = (filename: string, content: string) => {
    const a = document.createElement("a");
    const mimeType = "text/csv;encoding:utf-8";

    // Based on https://stackoverflow.com/a/11572891
    // We add the BOM characters before the content to support non latin
    // characters in the downloaded file.
    const contentWithBOM = "\uFEFF" + content;

    if (URL && "download" in a) {
        // Chrome, Firefox, Edge, Safari 10.1: HTML5 anchor tag download
        const blob = new Blob([contentWithBOM], {type: mimeType});
        a.href = window.URL.createObjectURL(blob);
        a.setAttribute("download", filename);
        document.body?.appendChild(a);
        a.click();
        document.body?.removeChild(a);
        // @ts-expect-error - TS2339 - Property 'msSaveBlob' does not exist on type 'Navigator'.
    } else if (navigator.msSaveBlob) {
        // IE 11, Edge 12: proprietary Microsoft API
        const blob = new Blob([contentWithBOM], {type: mimeType});
        // @ts-expect-error - TS2339 - Property 'msSaveBlob' does not exist on type 'Navigator'.
        window.navigator.msSaveBlob(blob, filename);
    } else {
        // Safari 9: open the csv file as text in the current browser tab
        const encodedContent = encodeURIComponent(contentWithBOM);
        location.href = `data:application/octet-stream,${encodedContent}`;
    }
};

// CSV values need to be processed to ensure compatibility and security.
export const toCsvField = (
    value: any,
    protectAgainstCSVInjection = true,
): string => {
    let str = "" + value;

    // Values that start with an Excel/Google Sheets formula triggering
    // character should be prefixed with a tab.
    // See http://georgemauer.net/2017/10/07/csv-injection.html
    const prefixWithTab = protectAgainstCSVInjection && /^[-+=@]/.test(str);

    // Values that contain double quotes or commas, or that are to be prefixed
    // with a tab, should be wrapped in double quotes. Any double quotes
    // contained within the value should be escaped by another double quote.
    const wrapInQuotes = /[",]/.test(str) || prefixWithTab;

    if (prefixWithTab) {
        str = `\t${str}`;
    }
    if (wrapInQuotes) {
        str = `"${str.replace(/"/g, `""`)}"`;
    }
    return str;
};

// Construct a CSV file from the given headers and rows, optionally protecting
// against CSV injection by prepending values that would trigger Excel
// formulas with a tab character. The CSV fields are separated by commas and
// the rows are separated by newlines. Row values are escaped as needed. The
// number of columns in each row should match the number of columns in the
// header, though this isn't validated. CSV injection protection is important
// if the CSV file contains data entered by an untrusted third party (e.g. a
// student) that will be loaded by some associated but different user (e.g. a
// teacher).
export const toCsvContent = (
    headers: Array<string>,
    rows: Array<Array<any>>,
    protectAgainstCSVInjection = true,
): string => {
    const formattedHeaders = headers
        .map((v) => toCsvField(v, protectAgainstCSVInjection))
        .join(",");
    const formattedRows = rows
        .map((row) =>
            row.map((v) => toCsvField(v, protectAgainstCSVInjection)).join(","),
        )
        .join("\n");
    return [formattedHeaders, formattedRows].join("\n");
};
